"use client";

import styles from "./footer.module.css";
import { IconFacebook } from "./icons/IconFacebook";
import { IconInstagram } from "./icons/IconInstagram";
import { IconLinkedIn } from "./icons/IconLinkedIn";
import { IconX } from "./icons/IconX";
import { IconYoutube } from "./icons/IconYoutube";
import { IconGooglePlay } from "./icons/IconGooglePlay";
import { IconAppStore } from "./icons/IconAppStore";
import { IconButton } from "../icon-button";
import { IconAccessibility } from "@web-monorepo/dds-icons";
import type { FooterProps } from "./types/footer.types";

export const Footer: React.FC<FooterProps> = (props) => {
  const {
    title,
    description,
    menus,
    copyright,
    smallLinks,
    socialLinks,
    appStoreLinks,
    languageSelect,
    accessibilityLink,
  } = props;

  const accessibilityButton = accessibilityLink ? (
    <IconButton
      variant="soft"
      size="s"
      as="a"
      href={accessibilityLink}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Accessibility"
    >
      <IconAccessibility />
    </IconButton>
  ) : null;

  return (
    <div className="bg-surface dark:bg-neutral-950 text-content-primary dark:text-content-light">
      <footer className={`${styles.footer}`}>
        <div className={styles.footerTop}>
          <div className={styles.footerContent}>
            <div className={styles.missionStatement}>
              <h2 className={styles.missionStatementTitle}>{title}</h2>
              <p className={styles.missionStatementDescription}>{description}</p>
            </div>

            {accessibilityLink || languageSelect ? (
              <div className="hidden md:flex items-center justify-start gap-x-sm">
                {languageSelect}
                {accessibilityButton}
              </div>
            ) : null}
          </div>

          {appStoreLinks?.map((link, linkIndex) => {
            return (
              <a
                key={`footer-app-store-link-mobile-${linkIndex}`}
                href={link.href}
                className={styles.appStoreLinkMobile}
              >
                <span className={styles.appStoreLinkLabel}>{link.label}</span>
                {link.icon === "google" && <IconGooglePlay className={styles.appStoreIcon} />}
                {link.icon === "ios" && <IconAppStore className={styles.appStoreIcon} />}
              </a>
            );
          })}

          <div className={styles.footerColumns} data-col-count={menus.length}>
            {menus.map((menu, index) => {
              return (
                <div key={`footer-menu-${index}`} className={styles.footerColumn}>
                  <h3 className={styles.footerColumnTitle}>{menu.title}</h3>
                  <ul className={styles.footerColumnList}>
                    {menu.items.map((item, itemIndex) => {
                      return (
                        <li key={`footer-menu-item-${itemIndex}`}>
                          <a href={item.href} className={styles.footerColumnLink}>
                            {item.label}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.footerBottom}>
          {accessibilityLink || languageSelect ? (
            <div className="flex items-center justify-between gap-x-sm md:hidden">
              {languageSelect}
              {accessibilityButton}
            </div>
          ) : null}

          <div className={styles.footerBottomStart}>
            {socialLinks ? (
              <ul className={styles.socialLinks}>
                {socialLinks.map((link, linkIndex) => {
                  return (
                    <li key={`footer-social-link-${linkIndex}`}>
                      <a href={link.href}>
                        <span className={styles.socialLinkLabel}>{link.label}</span>
                        {link.icon === "facebook" && <IconFacebook className={styles.socialIcon} />}
                        {link.icon === "instagram" && <IconInstagram className={styles.socialIcon} />}
                        {link.icon === "linkedin" && <IconLinkedIn className={styles.socialIcon} />}
                        {link.icon === "x" && <IconX className={styles.socialIcon} />}
                        {link.icon === "youtube" && <IconYoutube className={styles.socialIcon} />}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
          <div className={`${styles.footerBottomCenter} text-content-secondary dark:text-chalk-500`}>
            <div className={styles.footerCopyright}>{copyright}</div>
            {smallLinks ? (
              <ul className={styles.smallLinks}>
                {smallLinks.map((link, linkIndex) => {
                  return (
                    <li key={`footer-small-link-${linkIndex}`}>
                      <a href={link.href} className={`${styles.smallLink} ${styles.footerColumnLink}`}>
                        {link.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
          <div className={styles.footerBottomEnd}>
            {appStoreLinks ? (
              <ul className={styles.appStoreLinks}>
                {appStoreLinks.map((link, linkIndex) => {
                  return (
                    <li key={`footer-app-store-link-${linkIndex}`} className={styles.appStoreLink}>
                      <a href={link.href}>
                        <span className={styles.appStoreLinkLabel}>{link.label}</span>
                        {link.icon === "google" && <IconGooglePlay className={styles.appStoreIcon} />}
                        {link.icon === "ios" && <IconAppStore className={styles.appStoreIcon} />}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </div>
      </footer>
    </div>
  );
};
